import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/common/Logo';

/**
 * Onboarding welcome step — brief intro between signup and connection setup.
 *
 * Signup already captured organizationName, teamSize, and use case, so this
 * page no longer re-collects them. It just sets expectations for the next
 * few minutes of setup and routes the user into the connection flow.
 */
const OnboardingWelcomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const orgName =
    user?.user_metadata?.organization_name || 'your team';
  const firstName = user?.user_metadata?.first_name || 'there';

  const handleContinue = () => {
    navigate('/onboarding/connection');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="large" withText={false} className="text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to Sparvi, {firstName}
          </h1>
          <p className="text-lg text-gray-600">
            Let&apos;s connect {orgName}&apos;s data warehouse and find your first
            quality issues.
          </p>
        </div>

        {/* Expectation Setting */}
        <div className="mt-4 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            In the next few minutes, we&apos;ll:
          </h3>
          <div className="space-y-2">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-700">
                Connect securely to your Snowflake or BigQuery warehouse
              </span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-700">
                Scan your most important tables
              </span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-700">
                Auto-generate validations from your observed data
              </span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm text-gray-700">
                Surface real data quality issues you can act on today
              </span>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleContinue}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Get Started
          </button>
        </div>

        {/* Time Indicator */}
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <ClockIcon className="h-4 w-4 mr-1" />
          <span>This should take about 10-15 minutes total</span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWelcomePage;
